import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, Building2, Mail, Phone, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn } from '@utils';
import api from '@services/api';
import type { Tenant } from '@types';
import { useState } from 'react';

const tenantSchema = z.object({
  name: z.string().min(2, 'Organization name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  address: z.string().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
});

type TenantFormData = z.infer<typeof tenantSchema>;

export default function SettingsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings-tenant'],
    queryFn: () => api.get<{ data: Tenant }>('/settings/tenant'),
  });

  const tenant = data?.data;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
  });

  useEffect(() => {
    if (tenant) {
      reset({
        name: tenant.name || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        address: '',
        primary_color: tenant.primaryColor || '#3B82F6',
        secondary_color: tenant.secondaryColor || '#10B981',
      });
    }
  }, [tenant, reset]);

  const onSubmit = async (data: TenantFormData) => {
    setIsSubmitting(true);
    try {
      await api.put('/settings/tenant', data);
      toast.success('Settings saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-32 dark:bg-gray-700" />
        <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto space-y-6"
    >
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">General Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your organization settings</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Organization
          </h2>

          <div>
            <label htmlFor="name" className="label">Organization Name *</label>
            <input
              {...register('name')}
              type="text"
              id="name"
              className={cn('input', errors.name && 'input-error')}
            />
            {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="label">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className={cn('input', errors.email && 'input-error')}
              />
              {errors.email && <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="phone" className="label">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone
              </label>
              <input {...register('phone')} type="tel" id="phone" className="input" />
            </div>
          </div>

          <div>
            <label htmlFor="address" className="label">
              <MapPin className="w-4 h-4 inline mr-1" />
              Address
            </label>
            <textarea {...register('address')} id="address" rows={2} className="input" />
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Theme Colors</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="primary_color" className="label">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  {...register('primary_color')}
                  type="color"
                  id="primary_color"
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  {...register('primary_color')}
                  type="text"
                  className="input flex-1"
                  placeholder="#3B82F6"
                  aria-label="Primary Color Hex"
                />
              </div>
            </div>
            <div>
              <label htmlFor="secondary_color" className="label">Secondary Color</label>
              <div className="flex items-center gap-3">
                <input
                  {...register('secondary_color')}
                  type="color"
                  id="secondary_color"
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  {...register('secondary_color')}
                  type="text"
                  className="input flex-1"
                  placeholder="#10B981"
                  aria-label="Secondary Color Hex"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Plan Info (read-only) */}
        {tenant?.plan && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Current Plan</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{tenant.plan.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{tenant.plan.description || 'Active subscription'}</p>
              </div>
              <span className="badge bg-primary-100 text-primary-800 border-primary-200 capitalize">
                {tenant.subscriptionStatus}
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
